
import { 
  DollarSign, 
  CreditCard, 
  Calculator, 
  TrendingUp, 
  Receipt, 
  PiggyBank,
  Zap,
  Calendar,
  Building,
  FileText,
  BarChart3,
  Shield,
  ShoppingCart,
  Eye,
  Users2,
  Leaf,
  Banknote,
  Target,
  FileCheck,
  Landmark,
  PieChart
} from 'lucide-react';
import type { Product } from './types';

export const financialProducts: Product[] = [
  {
    id: 'comprehensive-accounting-suite',
    title: 'Comprehensive Accounting Suite',
    subtitle: 'Complete financial management',
    description: 'Full-featured accounting platform with automated bookkeeping, financial reporting, tax compliance, and multi-currency support designed for UK businesses with HMRC integration.',
    icon: Calculator,
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'Automated bookkeeping with UK accounting standards',
      'Financial reporting with Companies House compliance',
      'Tax compliance including VAT, Corporation Tax & PAYE',
      'Multi-currency support with UK £ as base currency',
      'Bank reconciliation with UK banking integration',
      'Expense tracking',
      'Invoice management',
      'Audit trails'
    ],
    pricing: { setup: 42000, monthly: 1932 },
    color: 'from-green-500 to-emerald-600',
    businessTypes: ['#accounting', '#bookkeeping', '#financial-management', '#tax-compliance', '#reporting']
  },
  {
    id: 'payment-processing-gateway',
    title: 'Payment Processing Gateway',
    subtitle: 'Secure payment solutions',
    description: 'Advanced payment processing platform with multiple gateway support, fraud protection, and comprehensive transaction management optimised for UK payment systems.',
    icon: CreditCard,
    image: 'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'Multiple payment gateways including UK providers (Worldpay, Barclays)',
      'Fraud protection with UK banking security standards',
      'Transaction management with FCA compliance',
      'Recurring billing with UK Direct Debit integration',
      'Mobile payments including Apple Pay & Google Pay UK',
      'PCI compliance',
      'Real-time processing',
      'Detailed reporting'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-blue-500 to-indigo-600',
    businessTypes: ['#payment-processing', '#fraud-protection', '#billing', '#transactions', '#pci-compliance']
  },
  {
    id: 'financial-planning-analytics',
    title: 'Financial Planning & Analytics',
    subtitle: 'Strategic financial insights',
    description: 'Advanced financial planning platform with budgeting, forecasting, scenario analysis, and investment tracking designed for UK financial regulations and market conditions.',
    icon: TrendingUp,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'Budget planning',
      'Financial forecasting',
      'Scenario analysis',
      'Investment tracking',
      'Cash flow management',
      'ROI analysis',
      'Risk assessment',
      'Performance metrics'
    ],
    pricing: { setup: 45000, monthly: 2070 },
    color: 'from-purple-500 to-pink-600',
    businessTypes: ['#financial-planning', '#budgeting', '#forecasting', '#investment-tracking', '#analytics']
  },
  {
    id: 'expense-management-system',
    title: 'Expense Management System',
    subtitle: 'Automated expense tracking',
    description: 'Comprehensive expense management with receipt scanning, approval workflows, mileage tracking, and expense reporting optimised for UK tax and compliance requirements.',
    icon: Receipt,
    image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'Receipt scanning',
      'Approval workflows',
      'Mileage tracking',
      'Expense categorization',
      'Policy compliance',
      'Mobile app',
      'Integration capabilities',
      'Detailed reporting'
    ],
    pricing: { setup: 32000, monthly: 1472 },
    color: 'from-orange-500 to-red-600',
    businessTypes: ['#expense-management', '#receipt-scanning', '#approval-workflows', '#mileage-tracking', '#compliance']
  },
  {
    id: 'investment-portfolio-manager',
    title: 'Investment Portfolio Manager',
    subtitle: 'Portfolio optimisation platform',
    description: 'Professional investment management platform with portfolio tracking, risk analysis, performance reporting, and automated rebalancing designed for UK financial markets and FCA regulations.',
    icon: PiggyBank,
    image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'Portfolio tracking',
      'Risk analysis',
      'Performance reporting',
      'Automated rebalancing',
      'Asset allocation',
      'Benchmark comparison',
      'Tax optimisation with UK capital gains & dividend tax rules',
      'Client reporting'
    ],
    pricing: { setup: 55000, monthly: 2530 },
    color: 'from-teal-500 to-cyan-600',
    businessTypes: ['#investment-management', '#portfolio-tracking', '#risk-analysis', '#asset-allocation', '#wealth-management']
  },
  {
    id: 'integrated-sales-payment-system',
    title: 'Integrated Sales & Payment System',
    subtitle: 'Seamless transaction management',
    description: 'Complete sales and payment solution with CRM integration, automated invoicing, and advanced financial reporting.',
    icon: DollarSign,
    image: 'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'CRM & sales pipeline management',
      'Automated invoicing & billing',
      'Multi-payment gateway support',
      'Financial reporting & analytics',
      'Subscription management',
      'Tax compliance automation',
      'Customer payment tracking',
      'Revenue forecasting'
    ],
    pricing: { setup: 40000, monthly: 1840 },
    color: 'from-yellow-500 to-orange-600',
    businessTypes: ['#sales', '#payments', '#crm', '#invoicing', '#financial-management']
  },
  {
    id: 'ai-accounts-payable',
    title: 'AI-Powered Accounts Payable',
    subtitle: 'Automated invoice processing',
    description: 'Automates invoice processing, approval workflows, and payment execution with machine learning and AI.',
    icon: Zap,
    image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'AI-powered invoice data extraction',
      'Automated approval workflows',
      'Duplicate invoice detection',
      'Vendor management & onboarding',
      'Payment scheduling & execution',
      'Exception handling & routing',
      'Real-time spend analytics',
      'Integration with ERP systems',
      'Fraud detection & prevention',
      'Audit trail & compliance'
    ],
    pricing: { setup: 48000, monthly: 2208 },
    color: 'from-blue-500 to-indigo-600',
    businessTypes: ['#accounts-payable', '#ai-automation', '#invoice-processing', '#workflow-automation', '#financial-management']
  },
  {
    id: 'financial-close-automation',
    title: 'Financial Close Automation',
    subtitle: 'Period-end process optimization',
    description: 'Streamlines the period-end closing process with task management, reconciliation, and compliance controls.',
    icon: Calendar,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'Automated close process workflows',
      'Task assignment & tracking',
      'Account reconciliation automation',
      'Journal entry automation',
      'Variance analysis & reporting',
      'Compliance checklist management',
      'Real-time close progress monitoring',
      'Integration with GL systems',
      'Close calendar & scheduling',
      'Audit-ready documentation'
    ],
    pricing: { setup: 52000, monthly: 2392 },
    color: 'from-purple-500 to-pink-600',
    businessTypes: ['#financial-close', '#period-end-processing', '#accounting-automation', '#compliance', '#financial-reporting']
  },
  {
    id: 'corporate-card-management',
    title: 'Corporate Card Management',
    subtitle: 'Smart expense card platform',
    description: 'Provides virtual and physical cards with real-time expense tracking and automated policy enforcement.',
    icon: CreditCard,
    image: 'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'Virtual & physical card issuance',
      'Real-time expense tracking',
      'Automated policy enforcement',
      'Receipt capture & matching',
      'Spending controls & limits',
      'Integration with accounting systems',
      'Expense categorization',
      'Approval workflow automation',
      'Fraud protection & monitoring',
      'Detailed spending analytics'
    ],
    pricing: { setup: 36000, monthly: 1656 },
    color: 'from-green-500 to-teal-600',
    businessTypes: ['#corporate-cards', '#expense-management', '#spend-control', '#payment-automation', '#financial-controls']
  },
  {
    id: 'revenue-recognition-automation',
    title: 'Revenue Recognition Automation',
    subtitle: 'Automated accounting compliance',
    description: 'Applies accounting standards to automatically recognize revenue from contracts and subscriptions.',
    icon: Building,
    image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'ASC 606 & IFRS 15 compliance',
      'Contract lifecycle management',
      'Automated revenue calculations',
      'Performance obligation tracking',
      'Deferred revenue management',
      'Revenue forecasting & planning',
      'Audit trail & documentation',
      'Integration with billing systems',
      'Multi-currency support',
      'Revenue analytics & reporting'
    ],
    pricing: { setup: 58000, monthly: 2668 },
    color: 'from-orange-500 to-red-600',
    businessTypes: ['#revenue-recognition', '#accounting-standards', '#compliance', '#subscription-billing', '#contract-management']
  },
  {
    id: 'financial-forecasting-platform',
    title: 'Financial Forecasting Platform',
    subtitle: 'Predictive financial planning',
    description: 'Uses historical data and market signals to predict future financial performance with scenario modeling.',
    icon: TrendingUp,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'Predictive financial modeling',
      'Scenario planning & what-if analysis',
      'Cash flow forecasting',
      'Revenue & expense projections',
      'Rolling forecasts & updates',
      'Budget vs. actual analysis',
      'KPI tracking & monitoring',
      'Integration with financial systems',
      'Monte Carlo simulations',
      'Executive dashboard & reporting'
    ],
    pricing: { setup: 50000, monthly: 2300 },
    color: 'from-cyan-500 to-blue-600',
    businessTypes: ['#financial-forecasting', '#budgeting', '#scenario-planning', '#financial-planning', '#predictive-analytics']
  },
  {
    id: 'tax-compliance-automation',
    title: 'Tax Compliance Automation',
    subtitle: 'Multi-jurisdiction tax management',
    description: 'Calculates, files, and remits taxes across jurisdictions with changing regulations and requirements.',
    icon: FileText,
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'Multi-jurisdiction tax calculations',
      'Automated tax filing & submissions',
      'Sales tax & VAT management',
      'Tax regulation updates & compliance',
      'Audit support & documentation',
      'Tax provision calculations',
      'Exemption certificate management',
      'Integration with ERP systems',
      'Real-time tax determination',
      'Tax reporting & analytics'
    ],
    pricing: { setup: 46000, monthly: 2116 },
    color: 'from-red-500 to-pink-600',
    businessTypes: ['#tax-compliance', '#sales-tax', '#vat-management', '#tax-automation', '#multi-jurisdiction']
  },
  {
    id: 'procurement-optimization',
    title: 'Procurement Optimization',
    subtitle: 'Strategic sourcing platform',
    description: 'Streamlines the purchasing process with supplier management, contract compliance, and spending analytics.',
    icon: ShoppingCart,
    image: 'https://images.unsplash.com/photo-1556155092-8707de31f9c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'Supplier management & onboarding',
      'Purchase requisition automation',
      'Contract management & compliance',
      'Spend analysis & optimization',
      'Vendor performance tracking',
      'E-procurement & catalog management',
      'Approval workflow automation',
      'Cost savings identification',
      'Risk management & monitoring',
      'Integration with financial systems'
    ],
    pricing: { setup: 44000, monthly: 2024 },
    color: 'from-teal-500 to-cyan-600',
    businessTypes: ['#procurement', '#supplier-management', '#spend-optimization', '#contract-management', '#purchasing']
  },
  {
    id: 'financial-controls-monitoring',
    title: 'Financial Controls Monitoring',
    subtitle: 'Continuous compliance oversight',
    description: 'Continuously monitors financial transactions and systems for anomalies, errors, or control violations.',
    icon: Shield,
    image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'Continuous transaction monitoring',
      'Control testing & validation',
      'Anomaly detection & alerts',
      'Segregation of duties monitoring',
      'Approval workflow tracking',
      'Exception reporting & management',
      'Compliance dashboard & metrics',
      'Risk assessment & scoring',
      'Audit trail & documentation',
      'Integration with financial systems'
    ],
    pricing: { setup: 55000, monthly: 2530 },
    color: 'from-violet-500 to-purple-600',
    businessTypes: ['#financial-controls', '#compliance-monitoring', '#risk-management', '#internal-audit', '#fraud-prevention']
  },
  {
    id: 'investor-relations-platform',
    title: 'Investor Relations Platform',
    subtitle: 'Stakeholder communication hub',
    description: 'Manages communications and reporting to investors with secure portals and performance analytics.',
    icon: Users2,
    image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'Investor portal & communication',
      'Financial reporting & disclosures',
      'Earnings call management',
      'Document distribution & access',
      'Investor database & CRM',
      'Performance metrics tracking',
      'Regulatory filing support',
      'Meeting scheduling & management',
      'Analytics & engagement tracking',
      'Compliance & governance tools'
    ],
    pricing: { setup: 48000, monthly: 2208 },
    color: 'from-indigo-500 to-blue-600',
    businessTypes: ['#investor-relations', '#stakeholder-communication', '#financial-reporting', '#public-companies', '#governance']
  },
  {
    id: 'esg-reporting-analytics',
    title: 'ESG Reporting & Analytics',
    subtitle: 'Sustainability performance platform',
    description: 'Measures, reports, and improves environmental, social, and governance performance for stakeholder requirements.',
    icon: Leaf,
    image: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'ESG data collection & management',
      'Sustainability metrics tracking',
      'Carbon footprint calculation',
      'Regulatory reporting compliance',
      'Stakeholder communication',
      'Goal setting & progress tracking',
      'Benchmark & peer comparison',
      'Risk assessment & management',
      'Third-party verification support',
      'Executive ESG dashboards'
    ],
    pricing: { setup: 60000, monthly: 2760 },
    color: 'from-green-500 to-emerald-600',
    businessTypes: ['#esg', '#sustainability', '#environmental', '#social-impact', '#governance-reporting']
  },
  {
    id: 'treasury-cash-management',
    title: 'Treasury & Cash Management',
    subtitle: 'Enterprise liquidity optimization',
    description: 'Optimizes cash flow, manages banking relationships, and provides visibility into global cash positions.',
    icon: Banknote,
    image: 'https://images.unsplash.com/photo-1565514020179-026b92b84bb6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'Global cash visibility',
      'Cash flow forecasting',
      'Bank account management',
      'Payment initiation & tracking',
      'FX exposure management',
      'Investment management',
      'Debt & borrowing management',
      'Bank relationship management',
      'Compliance & controls',
      'Treasury analytics & reporting'
    ],
    pricing: { setup: 65000, monthly: 2990 },
    color: 'from-emerald-500 to-teal-600',
    businessTypes: ['#treasury', '#cash-management', '#liquidity', '#fx-management', '#banking']
  },
  {
    id: 'budget-planning-software',
    title: 'Budget Planning Software',
    subtitle: 'Collaborative budgeting platform',
    description: 'Enables collaborative budget creation, approval workflows, and variance analysis across the organisation.',
    icon: Target,
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'Collaborative budget creation',
      'Approval workflow automation',
      'Variance analysis & reporting',
      'Version control & history',
      'Department budget management',
      'Rolling budget support',
      'Integration with GL systems',
      'Budget vs. actual tracking',
      'Scenario modeling',
      'Executive budget dashboards'
    ],
    pricing: { setup: 42000, monthly: 1932 },
    color: 'from-blue-500 to-indigo-600',
    businessTypes: ['#budgeting', '#planning', '#financial-management', '#variance-analysis', '#collaborative-planning']
  },
  {
    id: 'financial-reporting-suite',
    title: 'Financial Reporting Suite',
    subtitle: 'Automated statutory reporting',
    description: 'Automates financial statement preparation, statutory reporting, and regulatory filings with audit trails.',
    icon: FileCheck,
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'Automated financial statements',
      'Statutory reporting compliance',
      'Regulatory filing automation',
      'Consolidation & elimination',
      'Multi-GAAP support',
      'Disclosure management',
      'Audit trail & controls',
      'Report scheduling & distribution',
      'Data validation & accuracy',
      'Analyst-ready reports'
    ],
    pricing: { setup: 50000, monthly: 2300 },
    color: 'from-purple-500 to-indigo-600',
    businessTypes: ['#financial-reporting', '#statutory-reporting', '#regulatory-filings', '#compliance', '#consolidation']
  },
  {
    id: 'bank-connectivity-platform',
    title: 'Bank Connectivity Platform',
    subtitle: 'Multi-bank integration hub',
    description: 'Provides secure connectivity to multiple banks for real-time balance reporting and payment initiation.',
    icon: Landmark,
    image: 'https://images.unsplash.com/photo-1501167786227-4cba60f6d58f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'Multi-bank connectivity',
      'Real-time balance reporting',
      'Payment initiation & tracking',
      'Bank statement reconciliation',
      'Format translation & mapping',
      'Security & encryption',
      'Audit trail & logging',
      'Error handling & alerts',
      'API integration support',
      'Global bank network'
    ],
    pricing: { setup: 45000, monthly: 2070 },
    color: 'from-cyan-500 to-blue-600',
    businessTypes: ['#banking', '#connectivity', '#payments', '#reconciliation', '#multi-bank']
  },
  {
    id: 'profitability-analysis-platform',
    title: 'Profitability Analysis Platform',
    subtitle: 'Product & customer profitability',
    description: 'Analyses profitability at granular levels including product, customer, channel, and region dimensions.',
    icon: PieChart,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Financial & Business Intelligence',
    status: 'Available',
    features: [
      'Product profitability analysis',
      'Customer profitability tracking',
      'Channel margin analysis',
      'Regional profit comparison',
      'Activity-based costing',
      'Cost allocation models',
      'Pricing optimization',
      'Margin improvement insights',
      'What-if scenario analysis',
      'Executive profitability dashboards'
    ],
    pricing: { setup: 48000, monthly: 2208 },
    color: 'from-violet-500 to-purple-600',
    businessTypes: ['#profitability', '#cost-analysis', '#pricing-optimization', '#margin-analysis', '#business-intelligence']
  }
];
